// src/utils/meetingEncryptor.js

import CryptoJS from "crypto-js";

/*
|--------------------------------------------------------------------------
| Caregility Outpatient Meeting Encryptor
|--------------------------------------------------------------------------
|
| Generates encrypted provider and patient meeting links for the
| outpatient scheduling demo.
|
| Provider UserType = 1
| Patient UserType = 2
|
|--------------------------------------------------------------------------
*/

const API_USERNAME = "e8StCA";
const SECRET_KEY = "9tH0WsxD1Ko";

const BASE_URL =
  "https://servicebe-epic.caregility.net/api/meeting?api_username=" +
  API_USERNAME +
  "&data=";

function processEncryptionKey(secret) {
  const utf8Secret = CryptoJS.enc.Utf8.parse(secret);
  const baseHash = CryptoJS.SHA1(utf8Secret);

  const buffer = new Uint8Array(64).fill(0x36);

  for (let i = 0; i < 20; i++) {
    buffer[i] ^=
      (baseHash.words[i >>> 2] >>>
        (24 - ((i % 4) * 8))) &
      0xff;
  }

  const finalHash = CryptoJS.SHA1(
    CryptoJS.lib.WordArray.create(buffer)
  );

  return CryptoJS.lib.WordArray.create(
    finalHash.clone().words.slice(0, 4),
    16
  );
}

function encrypt(text) {
  const key = processEncryptionKey(SECRET_KEY);

  const iv = CryptoJS.enc.Hex.parse(
    "00000000000000000000000000000000"
  );

  return CryptoJS.AES.encrypt(text, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
}

function unixTimestamp() {
  return Math.floor(Date.now() / 1000);
}

/*
|--------------------------------------------------------------------------
| Round to nearest 15 minute appointment
|--------------------------------------------------------------------------
*/

function getAppointmentTime() {
  const d = new Date();

  let minutes = d.getMinutes();

  minutes = Math.round(minutes / 15) * 15;

  if (minutes === 60) {
    d.setHours(d.getHours() + 1);
    minutes = 0;
  }

  d.setMinutes(minutes);
  d.setSeconds(0);

  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
}

/*
|--------------------------------------------------------------------------
| Create Session ID
|--------------------------------------------------------------------------
*/

function createSessionId() {
  return (
    "demo-" +
    Date.now() +
    "-" +
    Math.floor(Math.random() * 9999)
  );
}

/*
|--------------------------------------------------------------------------
| Shared session
|--------------------------------------------------------------------------
*/

export function getCurrentSessionId() {
  if (!window.demoMeetingSession) {
    window.demoMeetingSession = createSessionId();
  }

  return window.demoMeetingSession;
}

export function clearCurrentSession() {
  window.demoMeetingSession = null;
}

/*
|--------------------------------------------------------------------------
| Main Generator
|--------------------------------------------------------------------------
*/

export function generateMeetingURL({
  userType = 1,
  username = "Dr. Halpert",
  sessionId = getCurrentSessionId(),
  userId = 234373,
}) {
  const payload =
    `instant=${unixTimestamp()}` +
    `&sessionid=${sessionId}` +
    `&usertype=${userType}` +
    `&username=${username}` +
    `&appttime=${getAppointmentTime()}` +
    `&userid=${userId}`;

  const encrypted = encrypt(payload);

  return BASE_URL + encodeURIComponent(encrypted);
}

/*
|--------------------------------------------------------------------------
| Convenience Helpers
|--------------------------------------------------------------------------
*/

export function generateProviderMeetingURL(
  provider = "Dr. Halpert"
) {
  return generateMeetingURL({
    userType: 1,
    username: provider,
  });
}

export function generatePatientMeetingURL(
  patient = "John Smith"
) {
  return generateMeetingURL({
    userType: 2,
    username: patient,
  });
}